package com.macrofocus.eflux.network

import com.macrofocus.common.units.Quantity
import com.macrofocus.eflux.network.logical.logicalBranchIdOf
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.physical.Branch
import com.macrofocus.eflux.network.physical.BranchId
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import kotlin.reflect.KClass

open class DerivedBranchColumn(val name: String, val longName: String, override val type: KClass<*>, private val value: (Branch) -> Any?, private val formattedValue: (Branch) -> String? = { value(it)?.toString() }) : RowMajorDataFrame.Column<Branch,Any?> {
    fun getFormattedValue(row: Branch): String? {
        return formattedValue(row)
    }

    override fun getValue(row: Branch): Any? {
        return value(row)
    }

    override fun toString(): String {
        return "$name"
    }

    companion object {
        val datetime = DerivedBranchColumn("datetime", "Date/Time", Any::class, { it.record.datetime })

        val logicalId = DerivedBranchColumn("logicalBranchId", "Logical Branch Identifier", String::class, { logicalBranchIdOf(logicalBusIdOf(it.from.id), logicalBusIdOf(it.to.id), it.record.ckt).name })
        val id = DerivedBranchColumn("branchId", "Branch Identifier", BranchId::class, { it.id })

        val ckt = DerivedBranchColumn("ckt", "Circuit Identifier", String::class, { it.ckt })

        val sbase = DerivedBranchColumn("sbase", "SBASE", Double::class, { it.sbase })

        val longName = DerivedBranchColumn("longName", "Name", String::class, { it.longName })

        val from = DerivedBranchColumn("from", "From", String::class, { it.from.id })
        val to = DerivedBranchColumn("to", "To", String::class, { it.to.id })

        val met = DerivedBranchColumn("met", "MET", Int::class, { it.met })
        val met_k = DerivedBranchColumn("met_k", "Metered From", String::class, { it.met_k.id })
        val met_m = DerivedBranchColumn("met_m", "Metered To", String::class, { it.met_m.id })

        val zone_k = DerivedBranchColumn("zone_k", "Zone From", String::class, { it.zone_k?.name })
        val zone_m = DerivedBranchColumn("zone_m", "Zone To", String::class, { it.zone_m?.name })
        val zone = DerivedBranchColumn("zone", "Zone", String::class, {
            if(it.zone_k?.name == "CH") {
                it.zone_m?.name
            } else {
                it.zone_k?.name
            }
        })
        val crossBorder = DerivedBranchColumn("crossBorder", "Cross-border", Boolean::class, { it.crossBorder })

        val va_k = DerivedBranchColumn("va_k", "Voltage Angle From", Quantity::class, { it.va_k })
        val va_m = DerivedBranchColumn("va_m", "Voltage Angle To", Quantity::class, { it.va_m })

        val va = DerivedBranchColumn("va", "Voltage Angle", Quantity::class, { it.va })
        val dva = DerivedBranchColumn("dva", "Δ[Voltage Angle]", Quantity::class, { it.dva }, { it.dva.amount.toFixed(4) + " " + it.dva.unit?.symbol })

        val v_k = DerivedBranchColumn("v_k", "Voltage From", Quantity::class, { it.v_k }, { it.v_k.amount.toFixed(2) + " " + it.v_k.unit?.symbol })
        val v_m = DerivedBranchColumn("v_m", "Voltage To", Quantity::class, { it.v_m }, { it.v_m.amount.toFixed(2) + " " + it.v_m.unit?.symbol })

        val vm_k = DerivedBranchColumn("vm_k", "Voltage Magnitude From", Quantity::class, { it.vm_k }, { it.vm_k.amount.toFixed(5) + " " + it.vm_k.unit?.symbol })
        val vm_m = DerivedBranchColumn("vm_m", "Voltage Magnitude To", Quantity::class, { it.vm_m }, { it.vm_m.amount.toFixed(5) + " " + it.vm_m.unit?.symbol })

        val vm = DerivedBranchColumn("vm", "Voltage Magnitude", Quantity::class, { it.vm })
        val dvm = DerivedBranchColumn("dvm", "Δ[Voltage Magnitude]", Quantity::class, { it.dvm })

        val r = DerivedBranchColumn("r", "Resistance R", Quantity::class, { it.record.r }, { it.r.amount.toFixed(3) + " " + it.r.unit?.symbol })
        val x = DerivedBranchColumn("x", "Reactance X", Quantity::class, { it.x }, { it.x.amount.toFixed(3) + " " + it.x.unit?.symbol })

        val dp = DerivedBranchColumn("dp", "Directional Power P", Quantity::class, { it.dp }, { it.dp.amount.toFixed(2) + " " + it.dp.unit?.symbol })
        val p = DerivedBranchColumn("p", "Power P", Quantity::class, { it.p }, { it.p.amount.toFixed(0) + " " + it.p.unit?.symbol })
        val q = DerivedBranchColumn("q", "Power Q", Quantity::class, { it.q }, { it.q.amount.toFixed(0) + " " + it.q.unit?.symbol })
        val abs_s = DerivedBranchColumn("abs_s", "Power abs(S)", Quantity::class, { it.abs_s })

        val ratea = DerivedBranchColumn("ratea", "Rated Power", Quantity::class, { it.ratea }, { it.ratea.amount.toFixed(0) + " " + it.ratea.unit?.symbol })
        val rateb = DerivedBranchColumn("rateb", "RATEB", Quantity::class, { it.rateb })
        val ratec = DerivedBranchColumn("ratec", "RATEC", Quantity::class, { it.ratec })

        val pratea = DerivedBranchColumn("pratea", "P / [Rated Power]", Double::class, { it.pratea }, { it.pratea.times(100.0)?.toFixed(1) + "%"})
        val onepratea = DerivedBranchColumn("onepratea", "1 – P / [Rated Power]", Double::class, { it.onepratea })
        val sratea = DerivedBranchColumn("sratea", "abs(S) / [Rated Power]", Double::class, { it.sratea })

        val qratea = DerivedBranchColumn("qratea", "Q / [Rated Power]", Double::class, { it.qratea },  { it.qratea.times(100.0)?.toFixed(3) + "%"})

        val baskv = DerivedBranchColumn("baskv", "Voltage level", Quantity::class, { it.baskv }, { it.baskv.amount.toFixed(0) + " " + it.baskv.unit?.symbol })

//        val branchId = DerivedBranchColumn("branchId", BranchId::class, { it.id })
//        val from = DerivedBranchColumn("from", BusId::class, { it.from.id })
//        val to = DerivedBranchColumn("to", BusId::class, { it.to.id })
//        val ckt = DerivedBranchColumn("ckt", String::class, { it.record.ckt })
//        val logical = DerivedBranchColumn("logical", Boolean::class, { logicalBusIdOf(it.from.id) != logicalBusIdOf(it.to.id) })
//        val logicalBranchId = DerivedBranchColumn("logicalBranchId", LogicalBranchId::class, { logicalBranchIdOf(logicalBusIdOf(it.from.id), logicalBusIdOf(it.to.id), it.record.ckt) })
//        val fromLogicalBusId = DerivedBranchColumn("fromLogicalBusId", LogicalBusId::class, { logicalBusIdOf(it.from.id) })
//        val toLogicalBusId = DerivedBranchColumn("toLogicalBusId", LogicalBusId::class, { logicalBusIdOf(it.to.id) })
//        val r = DerivedBranchColumn("r", Double::class, { it.record.r })
//        val x = DerivedBranchColumn("x", Double::class, { it.record.x })
//        val b = DerivedBranchColumn("b", Double::class, { it.record.b })
//        val ratea = DerivedBranchColumn("ratea", Double::class, { it.record.ratea })
//        val rateb = DerivedBranchColumn("rateb", Double::class, { it.record.rateb })
//        val ratec = DerivedBranchColumn("ratec", Double::class, { it.record.ratec })
//        val gi = DerivedBranchColumn("gi", Double::class, { it.record.gi })
//        val bi = DerivedBranchColumn("bi", Double::class, { it.record.bi })
//        val gj = DerivedBranchColumn("gj", Double::class, { it.record.gj })
//        val bj = DerivedBranchColumn("bj", Double::class, { it.record.bj })
        val st = DerivedBranchColumn("st", "Status", Int::class, { it.record.st })
//        val met = DerivedBranchColumn("met", Int::class, { it.record.met })
//        val len = DerivedBranchColumn("len", Double::class, { it.record.len })
//        val o1 = DerivedBranchColumn("o1", Double::class, { it.record.o1 })
//        val f1 = DerivedBranchColumn("f1", Double::class, { it.record.f1 })

        val all : Array<DerivedBranchColumn> = arrayOf(
//            id, sbase, met,met_k, met_m, va_k, va_m, va_km, va, u_k, u_m, x, p, q, abs_s, ratea, rateb, ratec, pratea, onepratea, sratea
            datetime, from, to, pratea, id, sbase, met, va_k, va_m, va, dva, vm_k, vm_m, x, p, q, abs_s, ratea, rateb, ratec, onepratea, sratea
        )

        val table : Array<DerivedBranchColumn> = arrayOf(
//            id, sbase, met,met_k, met_m, va_k, va_m, va_km, va, u_k, u_m, x, p, q, abs_s, ratea, rateb, ratec, pratea, onepratea, sratea
//            id, vm_k, vm_m, va_k, va_m, dva, x, p, q, abs_s, pratea, ratea, sratea
//            crossBorder, zone_k, zone_m, logicalId, longName, from, to, ckt, st, v_k, v_m, dva, r, x, p, dp, q, pratea, ratea, id
              zone, logicalId, from, to, st, baskv, p, ratea, pratea, r, x, id
        )

        val timeseries : Array<DerivedBranchColumn> = arrayOf(
//            id, st,  p, q, pratea, ratea, dva, r, x
            id, st, p, q, pratea, qratea
        )
    }
}

